import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestion } from "@fortawesome/free-solid-svg-icons";
import { SXModalBase } from "../modals/SXModal";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { openModal, closeModal, selectModalById } from "../../features/ui/uiSlice";

interface InfoButtonProps {
  /** Unique id for this info modal, e.g. "info-initial-bow-imperfection". Must be unique app-wide. */
  id: string;
  /** Modal header/title */
  title: string;
  /** Modal body content - text, images, figures, anything */
  children: React.ReactNode;
  /** Accessible label for the button itself */
  ariaLabel?: string;
  className?: string;
  /** Modal width - use "lg"/"xl" when the body includes figures */
  size?: "sm" | "lg" | "xl";
  /** Optional custom footer content. Receives a close callback when provided as a function. */
  footer?: React.ReactNode | ((close: () => void) => React.ReactNode);
}

/**
 * Small circular "?" button that opens a modal with explanatory content.
 * Modal visibility is tracked via the shared uiSlice modal registry
 * (same pattern as the other SX*Modal components), so `id` must be unique.
 */
export const InfoButton: React.FC<InfoButtonProps> = ({
  id,
  title,
  children,
  ariaLabel = "More information",
  className,
  size = "sm",
  footer,
}) => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector(selectModalById(id));
  const handleClose = () => dispatch(closeModal(id));

  const footerContent = typeof footer === "function" ? footer(handleClose) : footer;

  return (
    <>
      <button
        type="button"
        className={`info-button ${className ?? ""}`}
        style={{
          border: "1px solid #DFDFDF",
          background: "transparent",
          boxShadow: "none",
          WebkitAppearance: "none",
          appearance: "none",
        }}
        aria-label={ariaLabel}
        onClick={() => dispatch(openModal(id))}
      >
        <FontAwesomeIcon icon={faQuestion} style={{ fontSize: 10 }} />
      </button>

      <SXModalBase
        header={title}
        show={isOpen}
        size={size}
        close={handleClose}
        body={<>{children}</>}
        footer={footerContent ? <>{footerContent}</> : null}
      />
    </>
  );
};
